<p-progressBar *ngIf="isLoading" mode="indeterminate" styleClass="login-progress" [style]="{ height: '4px' }"></p-progressBar>

<main class="login-shell">
    <section class="login-identity" aria-label="LogiLink workspace overview">
        <div class="brand-block">
            <img src="/assets/logilink_logo_color.png" alt="LogiLink Logo">
            <div>
                <strong>LOGILINK</strong>
                <span>Enterprise logistics workspace</span>
            </div>
        </div>

        <div class="identity-copy">
            <p class="eyebrow">LogiLink ERP</p>
            <h1>Run shipments, finance, CRM, fleet, and warehouse work from one controlled desk.</h1>
            <p>
                Secure access for operations teams that need fast module switching, clean approvals, and live workspace context.
            </p>
        </div>

        <div class="workspace-panel">
            <div class="panel-header">
                <span>Today at a glance</span>
                <strong>Live workspace</strong>
            </div>
            <div class="metric-grid">
                <div>
                    <span>Shipments</span>
                    <strong>128</strong>
                </div>
                <div>
                    <span>Approvals</span>
                    <strong>17</strong>
                </div>
                <div>
                    <span>Invoices</span>
                    <strong>42</strong>
                </div>
                <div>
                    <span>Fleet jobs</span>
                    <strong>64</strong>
                </div>
            </div>
            <div class="module-strip">
                <span><i class="fa-solid fa-truck-fast"></i> Operations</span>
                <span><i class="fa-solid fa-cash-register"></i> Finance</span>
                <span><i class="fa-solid fa-boxes-stacked"></i> Warehousing</span>
            </div>
        </div>

        <div class="security-note">
            <i class="pi pi-shield"></i>
            <span>Protected sign-in with enterprise session controls.</span>
        </div>

        <div class="login-powered-by" aria-label="Powered by CargoMate">
            <span>Powered by</span>
            <img src="/assets/cargomate_logo.png" alt="CargoMate Logo">
        </div>
    </section>

    <section class="login-access" aria-label="Sign in">
        <div class="access-card">
            <div class="access-header">
                <div>
                    <span class="eyebrow">Secure sign in</span>
                    <h2 *ngIf="!isPhoneLogin">Welcome back</h2>
                    <h2 *ngIf="isPhoneLogin && !confirmationResult">Use {{ phoneOtpLabel }}</h2>
                    <h2 *ngIf="isPhoneLogin && confirmationResult">Verify your code</h2>
                </div>
                <span class="access-badge">ERP</span>
            </div>

            <p class="access-subtitle" *ngIf="!isPhoneLogin">
                Sign in to continue to your LogiLink workspace.
            </p>
            <p class="access-subtitle" *ngIf="isPhoneLogin && !confirmationResult">
                Enter a phone number with country code to receive a one-time passcode.
            </p>
            <p class="access-subtitle" *ngIf="isPhoneLogin && confirmationResult">
                {{ phoneOtpInstruction }}
            </p>

            <form *ngIf="!isPhoneLogin" (ngSubmit)="loginWithEmail()" #loginForm="ngForm" class="login-form">
                <div class="field-stack">
                    <label for="email">Email address</label>
                    <p-inputGroup>
                        <p-inputGroupAddon>
                            <i class="pi pi-envelope"></i>
                        </p-inputGroupAddon>
                        <input
                            id="email"
                            type="email"
                            pInputText
                            [(ngModel)]="email"
                            name="email"
                            autocomplete="email"
                            required
                            placeholder="name@company.com"
                        />
                    </p-inputGroup>
                </div>

                <div class="field-stack">
                    <label for="password">Password</label>
                    <p-inputGroup>
                        <p-inputGroupAddon>
                            <i class="pi pi-lock"></i>
                        </p-inputGroupAddon>
                        <input
                            id="password"
                            type="password"
                            pInputText
                            [(ngModel)]="password"
                            name="password"
                            autocomplete="current-password"
                            required
                            placeholder="Enter your password"
                        />
                    </p-inputGroup>
                </div>

                <div class="form-row">
                    <div class="remember-control">
                        <p-checkbox id="rememberMe" [(ngModel)]="rememberMe" name="rememberMe" [binary]="true"></p-checkbox>
                        <label for="rememberMe">Remember me</label>
                    </div>
                    <button type="button" class="link-button" (click)="forgotPassword()" [disabled]="isLoading">Forgot password?</button>
                </div>

                <p-button
                    label="Sign in"
                    type="submit"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    [disabled]="!loginForm.form.valid || isLoading"
                    styleClass="primary-action">
                </p-button>

                <div class="divider">
                    <span></span>
                    <em>or continue with</em>
                    <span></span>
                </div>

                <div class="alternate-actions">
                    <p-button
                        label="Google"
                        icon="pi pi-google"
                        type="button"
                        (click)="loginWithGoogle()"
                        [disabled]="isLoading"
                        styleClass="secondary-action">
                    </p-button>
                    <p-button
                        label="Microsoft"
                        icon="pi pi-microsoft"
                        type="button"
                        (click)="loginWithMicrosoft()"
                        [disabled]="isLoading"
                        styleClass="secondary-action">
                    </p-button>
                    <p-button
                        [label]="phoneOtpLabel"
                        icon="pi pi-mobile"
                        type="button"
                        (click)="loginWithPhone()"
                        [disabled]="isLoading"
                        styleClass="secondary-action">
                    </p-button>
                </div>
            </form>

            <div *ngIf="isPhoneLogin" class="login-form">
                <form *ngIf="!confirmationResult" (ngSubmit)="sendOtp()" #phoneForm="ngForm" class="login-form">
                    <div class="field-stack">
                        <label for="phoneNumber">Phone number</label>
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="pi pi-phone"></i>
                            </p-inputGroupAddon>
                            <input
                                id="phoneNumber"
                                type="tel"
                                pInputText
                                [(ngModel)]="phoneNumber"
                                name="phoneNumber"
                                autocomplete="tel"
                                required
                                placeholder="+16505551234"
                            />
                        </p-inputGroup>
                    </div>

                    <p-button
                        [label]="sendOtpLabel"
                        type="submit"
                        icon="pi pi-send"
                        iconPos="right"
                        [disabled]="!phoneForm.form.valid || isLoading"
                        styleClass="primary-action">
                    </p-button>
                </form>

                <form *ngIf="confirmationResult" (ngSubmit)="verifyOtpAndLogin()" #otpForm="ngForm" class="login-form">
                    <div class="field-stack">
                        <label for="otp">OTP code</label>
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="pi pi-key"></i>
                            </p-inputGroupAddon>
                            <input
                                id="otp"
                                type="text"
                                pInputText
                                [(ngModel)]="otp"
                                name="otp"
                                inputmode="numeric"
                                autocomplete="one-time-code"
                                required
                                placeholder="Enter verification code"
                            />
                        </p-inputGroup>
                    </div>

                    <p-button
                        label="Verify and sign in"
                        type="submit"
                        icon="pi pi-check"
                        iconPos="right"
                        [disabled]="!otpForm.form.valid || isLoading"
                        styleClass="primary-action">
                    </p-button>
                </form>

                <button type="button" class="back-button" (click)="switchToEmailLogin()">
                    <i class="pi pi-arrow-left"></i>
                    <span>Back to email login</span>
                </button>
            </div>

            <div id="recaptcha-container" class="recaptcha-slot"></div>

            <div class="trial-panel">
                <div>
                    <span>New workspace?</span>
                    <strong>Start an enterprise trial</strong>
                </div>
                <a pButton type="button" label="Start trial" icon="fa-solid fa-rocket" routerLink="/enterprise-subscription" styleClass="trial-action"></a>
            </div>
        </div>
    </section>
</main>
